import React, { useEffect, useState } from "react"
import { getAPIBaseURL } from "../../services/helpers"
import { getData } from "../../services/request"

const StatCard = ({ label, value, subtitle, icon }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-slate-600">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
        {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
      </div>
      {icon && <div className="text-slate-400">{icon}</div>}
    </div>
  </div>
)

const StatusBreakdown = ({ title, data, colors, total }) => {
  const totalValue = total || Object.values(data).reduce((sum, val) => sum + (val || 0), 0)
  
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{title}</h3>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-xs text-slate-600">{key.replace(/_/g, ' ')}</span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-24 rounded-full bg-slate-200">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${totalValue > 0 ? (value / totalValue) * 100 : 0}%`,
                    backgroundColor: colors[key] || '#64748B'
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-slate-900">{value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const AdminReports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [reports, setReports] = useState(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const fetchReports = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/v1/reports`
    const params = {}
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    
    try {
      const res = await getData(url, {}, params)
      setLoading(false)
      
      if (res.error === "") {
        if (res.result.status === 200) {
          setReports(res.result.data?.data || null)
        } else {
          setError(res.result.data?.message || 'Failed to load reports')
        }
      } else {
        setError(res.result?.data?.message || 'Network error')
      }
    } catch (err) {
      setLoading(false)
      setError('An unexpected error occurred')
    }
  }

  useEffect(() => {
    fetchReports()
  }, [startDate, endDate])

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const orderStatusColors = {
    INITIATED: '#3B82F6',
    PAID: '#10B981',
    TICKET_ISSUED: '#8B5CF6',
    CANCELLED: '#EF4444',
    REFUNDED: '#F59E0B',
    FAILED: '#DC2626'
  }

  const eventStatusColors = {
    PUBLISHED: '#10B981',
    DRAFT: '#F59E0B',
    ARCHIVED: '#6B7280'
  }

  const offerStatusColors = {
    ACTIVE: '#10B981',
    DRAFT: '#F59E0B',
    INACTIVE: '#6B7280'
  }

  const ticketStatusColors = {
    ACTIVE: '#10B981',
    USED: '#3B82F6',
    VOID: '#EF4444'
  }

  const paymentStatusColors = {
    pending: '#F59E0B',
    initiated: '#3B82F6',
    completed: '#10B981',
    failed: '#EF4444',
    cancelled: '#6B7280'
  }

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="text-slate-500 mt-1">Comprehensive system statistics and insights</p>
        </div>
        
        {/* Date Filter */}
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            placeholder="End Date"
          />
          {(startDate || endDate) && (
            <button
              onClick={handleClearFilter}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="mt-2 text-sm text-slate-500">Loading reports...</p>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">
          {error}
          <button onClick={fetchReports} className="block mt-2 text-sm font-bold hover:underline">Try Again</button>
        </div>
      ) : !reports ? (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No report data available
        </div>
      ) : (
        <>
          {/* User Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">User Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total Centers"
                value={reports.users?.centers?.total || 0}
                subtitle={`${reports.users?.centers?.active || 0} active, ${reports.users?.centers?.inactive || 0} inactive`}
              />
              <StatCard
                label="Total Attendees"
                value={reports.users?.attendees?.total || 0}
                subtitle={`${reports.users?.attendees?.active || 0} active, ${reports.users?.attendees?.inactive || 0} inactive`}
              />
              <StatCard
                label="Total Admins"
                value={reports.admins?.total || 0}
                subtitle={`${reports.admins?.active || 0} active, ${reports.admins?.inactive || 0} inactive`}
              />
              <StatCard
                label="Total Users"
                value={(reports.users?.centers?.total || 0) + (reports.users?.attendees?.total || 0)}
                subtitle="Centers + Attendees"
              />
            </div>
          </div>

          {/* Event Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Event Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard
                label="Total Events"
                value={reports.events?.total || 0}
              />
              <StatusBreakdown
                title="Events by Status"
                data={reports.events?.byStatus || {}}
                colors={eventStatusColors}
                total={reports.events?.total || 0}
              />
            </div>
          </div>

          {/* Offer Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Offer Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard
                label="Total Offers"
                value={reports.offers?.total || 0}
              />
              <StatusBreakdown
                title="Offers by Status"
                data={reports.offers?.byStatus || {}}
                colors={offerStatusColors}
                total={reports.offers?.total || 0}
              />
            </div>
          </div>

          {/* Order Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Order Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard
                label="Total Orders"
                value={reports.orders?.total || 0}
              />
              <StatusBreakdown
                title="Orders by Status"
                data={reports.orders?.byStatus || {}}
                colors={orderStatusColors}
                total={reports.orders?.total || 0}
              />
            </div>
          </div>

          {/* Ticket Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Ticket Statistics</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatCard
                label="Total Tickets"
                value={reports.tickets?.total || 0}
              />
              <StatusBreakdown
                title="Tickets by Status"
                data={reports.tickets?.byStatus || {}}
                colors={ticketStatusColors}
                total={reports.tickets?.total || 0}
              />
            </div>
          </div>

          {/* Payment Statistics */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Payment Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total Payments"
                value={reports.payments?.total || 0}
              />
              <StatCard
                label="Completed Payments"
                value={reports.payments?.byStatus?.completed || 0}
              />
              <StatCard
                label="Total Revenue"
                value={formatCurrency(reports.payments?.revenue?.total || 0)}
                subtitle={reports.payments?.revenue?.currency || 'NGN'}
              />
              <StatCard
                label="Average Payment"
                value={formatCurrency(reports.payments?.revenue?.average || 0)}
                subtitle={reports.payments?.revenue?.currency || 'NGN'}
              />
            </div>
            <div className="mt-4">
              <StatusBreakdown
                title="Payments by Status"
                data={reports.payments?.byStatus || {}}
                colors={paymentStatusColors}
                total={reports.payments?.total || 0}
              />
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Recent Activity (Last 7 Days)</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <StatCard
                label="New Centers"
                value={reports.recentActivity?.centers || 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Attendees"
                value={reports.recentActivity?.attendees || 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Events"
                value={reports.recentActivity?.events || 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Orders"
                value={reports.recentActivity?.orders || 0}
                subtitle="Last 7 days"
              />
              <StatCard
                label="New Payments"
                value={reports.recentActivity?.payments || 0}
                subtitle="Last 7 days"
              />
            </div>
          </div>

          {/* Date Range Info */}
          {(reports.dateRange?.startDate || reports.dateRange?.endDate) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <strong>Date Range:</strong>{" "}
              {reports.dateRange.startDate
                ? new Date(reports.dateRange.startDate).toLocaleDateString()
                : "Beginning"}{" "}
              to{" "}
              {reports.dateRange.endDate
                ? new Date(reports.dateRange.endDate).toLocaleDateString()
                : "Today"}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default AdminReports
